import React from 'react';
import { Language } from '../types/kundali';
import { AdvancedLifeAnalysisResult } from '../types/advancedLifeAnalysis';
import { Zap, Clock, Compass, Activity, CheckCircle2, Sparkles, AlertCircle } from 'lucide-react';

interface DashaGocharConvergenceCardProps {
  analysisResult: AdvancedLifeAnalysisResult;
  lang: Language;
  className?: string;
}

export const DashaGocharConvergenceCard: React.FC<DashaGocharConvergenceCardProps> = ({
  analysisResult,
  lang,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const { dashaTimeline, gocharAnalysis, convergenceMatrix } = analysisResult;

  return (
    <div
      id="dasha-gochar-convergence-card"
      className={`bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-2xs space-y-4 ${className}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-astro-border pb-3">
        <div>
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-astro-accent" />
            <h3 className="text-sm sm:text-base font-bold uppercase tracking-wider text-astro-text">
              {isHi ? 'दशा एवं गोचर संमिलन मैट्रिक्स' : 'Dasha & Gochar Convergence Matrix'}
            </h3>
          </div>
          <p className="text-xs text-astro-secondary-text mt-0.5">
            {isHi
              ? 'विंशोत्तरी दशा सक्रियता एवं गुरु-शनि द्वि-गोचर का सामयिक प्रभाव'
              : 'Vimshottari Dasha activation cross-referenced with Jupiter-Saturn double transit triggers.'}
          </p>
        </div>

        {/* Active Dasha Badge */}
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-astro-bg border border-astro-border text-astro-primary text-xs font-bold shadow-2xs">
            <Clock className="w-3.5 h-3.5 text-astro-primary" />
            <span>MD: {dashaTimeline.activeMD} | AD: {dashaTimeline.activeAD} | PD: {dashaTimeline.activePD}</span>
          </div>
        </div>
      </div>

      {/* Convergence Matrix Factor Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 pt-1">
        {convergenceMatrix.map((cf, idx) => (
          <div
            key={idx}
            className={`p-3.5 rounded-xl border flex flex-col justify-between space-y-2 transition-all ${
              cf.supporting
                ? 'bg-emerald-50/70 border-emerald-300'
                : 'bg-astro-bg border-astro-border'
            }`}
          >
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text">
                  {cf.factorType.replace('_', ' ')}
                </span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                  cf.strength === 'STRONG'
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-astro-teal-tint text-astro-primary'
                }`}>
                  {cf.strength}
                </span>
              </div>
              <h5 className="text-xs font-bold text-astro-text mb-1">
                {cf.name}
              </h5>
              <p className="text-[11px] text-astro-secondary-text leading-relaxed font-normal">
                {isHi ? cf.descriptionHi : cf.descriptionEn}
              </p>
            </div>

            <div className="pt-2 border-t border-astro-border flex items-center justify-between text-[10px] font-semibold">
              <span className="text-astro-secondary-text">{isHi ? 'प्रभाव:' : 'Impact:'}</span>
              <span className={cf.supporting ? 'text-emerald-800 font-bold' : 'text-astro-primary font-bold'}>
                {cf.impact}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Transit Conditions Footer Strip */}
      <div className="p-3 rounded-xl bg-astro-bg border border-astro-border flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-bold text-astro-text">
            {isHi ? 'प्रमुख गोचर स्थिति:' : 'Key Transit Placements:'}
          </span>
          <span className="font-medium text-astro-secondary-text">
            Saturn: <strong className="text-astro-text">{gocharAnalysis.saturnSign} (H{gocharAnalysis.saturnHouse})</strong>
          </span>
          <span className="font-medium text-astro-secondary-text">
            Jupiter: <strong className="text-astro-text">{gocharAnalysis.jupiterSign} (H{gocharAnalysis.jupiterHouse})</strong>
          </span>
          <span className="font-medium text-astro-secondary-text">
            Rahu/Ketu: <strong className="text-astro-text">{gocharAnalysis.rahuSign}/{gocharAnalysis.ketuSign}</strong>
          </span>
        </div>

        {gocharAnalysis.doubleTransitActive && (
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-300">
            <Sparkles className="w-3 h-3 text-astro-accent" />
            {isHi ? `गुरु-शनि द्वि-गोचर प्रभाव: भाव ${gocharAnalysis.doubleTransitHouses.join(', ')}` : `Double Transit Influenced Houses: ${gocharAnalysis.doubleTransitHouses.join(', ')}`}
          </span>
        )}
      </div>
    </div>
  );
};
